import { CompactCalendarEvent } from './types';

export const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const WEEKDAYS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];

export const getDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const startOfDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

/** Sunday of the week containing `date` (local civil date). */
export const getWeekStart = (date: Date): Date => {
    const day = startOfDay(date);
    day.setDate(day.getDate() - day.getDay());
    return day;
};

/**
 * Visible dayGridMonth range: Sunday of the first week through exclusive end
 * of the Saturday after the last day of the month (matches FullCalendar datesSet).
 */
export const getVisibleMonthRange = (year: number, month: number): { start: Date; end: Date } => {
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const start = getWeekStart(firstOfMonth);
    const lastWeekStart = getWeekStart(lastOfMonth);
    const end = addDays(lastWeekStart, 7); // exclusive
    return { start, end };
};

export const shiftMonth = (year: number, month: number, delta: number): { year: number; month: number } => {
    const d = new Date(year, month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
};

/**
 * Events whose civil span includes the given date key, ordered by start time to
 * match FullCalendar. End is exclusive at the instant, not at midnight of the
 * end date: a day is covered iff [start, end) overlaps [dayStart, nextDay).
 * Zero-duration (end <= start) still occupies the start day, like FullCalendar.
 */
export const eventsForDate = (events: CompactCalendarEvent[], dateKey: string): CompactCalendarEvent[] => {
    const dayStart = startOfDay(new Date(`${dateKey}T00:00:00`));
    const dayEnd = addDays(dayStart, 1);
    return events
        .filter((event) => {
            const { start, end } = event;
            if (end.getTime() <= start.getTime()) {
                return start.getTime() >= dayStart.getTime() && start.getTime() < dayEnd.getTime();
            }
            // Timed/multi-day: overlap of [start, end) with [dayStart, dayEnd)
            return start.getTime() < dayEnd.getTime() && end.getTime() > dayStart.getTime();
        })
        .sort((a, b) => a.start.getTime() - b.start.getTime());
};

export const groupEventsByDateKey = (
    events: CompactCalendarEvent[],
    start: Date,
    end: Date,
): Map<string, CompactCalendarEvent[]> => {
    const map = new Map<string, CompactCalendarEvent[]>();
    let cursor = startOfDay(start);
    const exclusiveEnd = startOfDay(end);

    while (cursor.getTime() < exclusiveEnd.getTime()) {
        const key = getDateKey(cursor);
        const dayEvents = eventsForDate(events, key);
        if (dayEvents.length > 0) {
            map.set(key, dayEvents);
        }
        cursor = addDays(cursor, 1);
    }

    return map;
};
